import React, { Fragment, Suspense, lazy, useEffect, useState } from "react";
import {
  HashRouter as Router,
  Redirect,
  Route,
  Switch,
  matchPath,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { useSelector } from "react-redux";

import { getTheme } from "./SharedComponents/ThemeComponent";
import { selectDarkMode } from "./Redux/Reducers/darkModeSlice";
import Authentication from "./Core/Authentication";
import Routes from "./Core/Routes/Routes";
import LoaderComponent from "./Core/Loader/LoaderComponent";
import RouteFallback, { NativeRouteLoader } from "./Core/Loader/RouteFallback";
import SidebarComponent from "./Components/SidebarComponent/SidebarComponent";
import HeaderBar from "./Components/HeaderBar/HeaderBar";
import AppHeaderBar from "./Components/HeaderBar/AppHeaderBar";
import NativeComponent from "./Components/NativeComponents/Index";
import TutorialRouteListener from "./Components/Courses/TutorialRouteListener";
import { TestAssignmentNotifier } from "./Components/TestAssignmentNotifier/TestAssignmentNotifier";

import useProtectedRoute from "./hooks/useProtectedRoute";
import useHomepageVersion from "./hooks/useHomepageVersion";
import HomepageUpgradeBanner from "./Components/HomepageUpgradeBanner/HomepageUpgradeBanner";
import SSOLanding from "./Components/SSOLanding/SSOLanding";
import {
  getAppRoutes,
  DetailedInsight,
  NewProjectPage,
  LandingPage,
} from "./utils/appRoutes";
import styles from "./styles.module.css";
import { checkIfPaidUser } from "./Core/utils/auth";
import { FeatureFlagProvider } from "./featureFlags";

const PreviewAlchemyFile = lazy(
  () => import("./Components/LearningContent/PreviewAlchemyFile"),
);
const Error = lazy(() => import("./Components/Error"));
const FeatureFlagsDebug = lazy(
  () => import("./Components/FeatureFlagsDebug/FeatureFlagsDebug"),
);

const ProtectedRoute = ({ onNavigate, hideNav }) => {
  const { state, derived, handlers } = useProtectedRoute();
  const { role, isSidebarVisible, location } = state;
  const { isDesktop, theme } = derived;
  const { handleToggle } = handlers;
  const [, setAuthVersion] = useState(0); // state only used to trigger re-render

  useEffect(() => {
    const handler = () => setAuthVersion((v) => v + 1);
    window.addEventListener("sandbox_user_update", handler);
    return () => window.removeEventListener("sandbox_user_update", handler);
  }, []);

  const isPaidStudent = checkIfPaidUser();
  const { isChoiceAvailable } = useHomepageVersion();

  const routes = getAppRoutes(role, isPaidStudent);

  const showHomepageBanner =
    isChoiceAvailable &&
    !hideNav &&
    !!matchPath(location.pathname, { path: "/home", exact: true });

  const isSectionsPath = location.pathname.includes("/sections");

  const outerBg = isSectionsPath
    ? "#FAF6F4"
    : theme.palette.background.background0;

  const innerBg = isSectionsPath
    ? "#FAF6F4"
    : role === "mentor" && location.pathname.includes("/home")
      ? theme.palette.background.mentorHomeBackground
      : location.pathname.includes("/workbench")
        ? theme.palette.background.workBenchBg
        : theme.palette.background.background0;

  const minHeight = location.pathname.includes("evaluation")
    ? "95vh"
    : location.pathname.includes("question-bank")
      ? ""
      : "100vh";

  const showNativeHeader =
    window.ReactNativeWebView &&
    !/^\/(evaluation(\/.*)?|new-learning-path|edit-demo)$/.test(
      location.pathname,
    );

  const isMatchFullPage = ["/sp-dashboard", "/home", "/sb/home"].find((path) =>
    matchPath(location.pathname, { path, exact: false }),
  );

  return (
    <div
      className={styles.protectedLayout}
      style={{ backgroundColor: outerBg }}
    >
      {isDesktop &&
        !window.ReactNativeWebView &&
        isSidebarVisible &&
        !hideNav && (
          <div className={styles.sidebarWrapper}>
            <SidebarComponent handleToggle={handleToggle} />
          </div>
        )}

      <div
        className={styles.contentArea}
        style={{
          backgroundColor: innerBg,
        }}
      >
        <div className={styles.contentBlock} style={{ minHeight }}>
          {!hideNav &&
            (!window.ReactNativeWebView ? (
              <HeaderBar handleToggle={handleToggle} />
            ) : showNativeHeader ? (
              <AppHeaderBar />
            ) : (
              <></>
            ))}
          {showHomepageBanner && <HomepageUpgradeBanner />}
          <Suspense fallback={<RouteFallback className={styles.fill} />}>
            <div
              style={{
                maxWidth: isMatchFullPage ? "100%" : "1440px",
                margin: isMatchFullPage ? "0" : "0 auto",
                padding: isMatchFullPage ? "0" : "0px 16px",
              }}
            >
              <Routes routes={routes} redirectTo="/home" />
            </div>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

function App({ onNavigate, hideNav }) {
  const darkModeTheme = useSelector(selectDarkMode);
  const theme = getTheme();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--scrollcolour",
      theme.palette.background.background3,
    );
    root.style.setProperty(
      "--scrolltrack",
      theme.palette.background.profileBackground,
    );
  }, [darkModeTheme]);

  const routerContent = (
    <>
      <TutorialRouteListener />
      <TestAssignmentNotifier />
      <Suspense
        fallback={
          window.ReactNativeWebView ? (
            <NativeRouteLoader className={styles.fill} />
          ) : (
            <div className={styles.fullViewport}>
              <LoaderComponent />
            </div>
          )
        }
      >
        <Switch>
          <Route path="/" component={LandingPage} exact />
          <Route path="/sso-login" component={SSOLanding} exact />
          <Route path="/new" component={NewProjectPage} exact />
          <Route path="/login" component={LandingPage} />
          <Route path="/previewFile/:id" component={PreviewAlchemyFile} />
          <Route path="/404" component={Error} exact />
          <Route
            path={[
              "/student/:id/1",
              "/student/:id/",
              "/user/:id/1",
              "/user/:id",
            ]}
            component={NewProjectPage}
            exact
            allow
          />
          <Route
            path="/evaluation/report/:assessmentId"
            component={DetailedInsight}
            exact
            allow
          />
          <Route path="/flags-debug" component={FeatureFlagsDebug} exact />
          {window.ReactNativeWebView && (
            <Route path="/native" component={NativeComponent} exact />
          )}
          <Authentication>
            <ProtectedRoute onNavigate={onNavigate} hideNav={hideNav} />
          </Authentication>
          <Redirect to="/" />
        </Switch>
      </Suspense>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      {/* Skip CssBaseline in embedded mode — it applies global resets
          (body margin, box-sizing) that would break portal's layout */}
      {!hideNav && <CssBaseline />}
      <Fragment>
        {/* In embedded mode, MemoryRouter is provided by index.js.
            In standalone mode, use HashRouter as before. */}
        <FeatureFlagProvider>
          {hideNav ? routerContent : <Router>{routerContent}</Router>}
        </FeatureFlagProvider>
      </Fragment>
    </ThemeProvider>
  );
}

export default Sentry.withProfiler(App);
